import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Backdrop, Grow, Modal } from '@material-ui/core';
import * as Utils from '../../commons/utils/Utils.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import { ICONO_CONFIRMAR } from '../../commons/dialog/Dialog.js';

/**
 * VEC-3318 — cómo se gestionó una línea: el historial de lo que se hizo con ella.
 *
 * Responde la pregunta de la pestaña Finalizadas: "¿y esto cómo terminó?". Una línea puede haberse
 * resuelto en varios pasos —dos despachos parciales y una cancelación por el resto— y el estado final
 * solo, que es lo único que muestra la grilla, no cuenta eso.
 *
 * El historial NO sale de una tabla de gestiones sino de `item_stock_movimientos`: el movimiento YA
 * es el registro del hecho (qué tipo, de dónde a dónde, cuánto, quién, cuándo y por qué). Una tabla
 * paralela repetiría todo eso y podría contradecirlo.
 *
 * CADA TIPO SE CUENTA DISTINTO, y no es cosmética: mostrar los tres con el mismo "origen → destino"
 * sería mentir en dos de ellos.
 *  - **Transferencia**: el stock se movió, así que el par origen → destino es el dato.
 *  - **Compra**: no hay origen —entró stock nuevo—, y una flecha desde un guion se lee como un dato
 *    que falta en vez de como que no existe.
 *  - **Cancelación**: no movió nada. El backend le graba el mismo depósito en los dos extremos
 *    (es el de la solicitud), así que dibujar "Almacén 3 → Almacén 3" sugiere un movimiento que no
 *    ocurrió. Lo que importa es cuánto se dio de baja y por qué.
 *
 * ESTRUCTURA BOOTSTRAP, NO `DialogTitle`/`DialogContent`/`DialogActions`.
 *
 * Esos tres traen sus PROPIOS paddings (`MuiDialogTitle-root`, `MuiDialogContent-root`,
 * `MuiDialogActions-root`) y se aplican ENCIMA de `modal-header` / `modal-body` / `modal-footer`,
 * así que el modal quedaba con márgenes distintos a los ~95 modales Bootstrap del sistema. Es lo que
 * advierte docs/design-system/dialogos-y-modales.md ("Evitar Dialog / Modal de Material-UI para
 * modales nuevos") y lo mismo que hizo descartar la primera versión de GridHistoryModal.
 *
 * `Modal` de MUI queda SOLO como contenedor —backdrop, portal y foco—, igual que en
 * TicketsGridExport. La caja la arma Bootstrap, y por eso el modal se alinea arriba y mide lo que
 * miden los demás.
 */

const BASE_URL = '/inventario/solicitudes/detalles';

const TIPO_TRANSFERENCIA = 'TRANSFERENCIA';
const TIPO_COMPRA = 'COMPRA';
const TIPO_CANCELACION = 'CANCELACION';

const InventarioSolicitudGestionesModal = (props) => {
  const intl = useIntl();

  const detalle = props.detalle;

  const [gestiones, setGestiones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!detalle) return undefined;

    // Guarda de secuencia: abrir una línea, cerrar y abrir otra rápido deja dos pedidos en vuelo, y
    // sin esto se pinta el que responda último y no el de la línea que se está mirando.
    let vigente = true;
    setCargando(true);
    setError(false);
    setGestiones([]);

    Utils.getData(BASE_URL + '/' + detalle.id + '/gestiones')
      .then((data) => {
        if (!vigente) return;
        setGestiones((data && data.gestiones) || []);
      })
      .catch(() => {
        if (vigente) setError(true);
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [detalle && detalle.id]);

  const etiquetaTipo = (tipo) => {
    const etiquetas = {
      TRANSFERENCIA: intl.formatMessage({
        id: 'inventarioSolicitudes.gestiones.tipo_transferencia',
        defaultMessage: 'Transferencia',
      }),
      COMPRA: intl.formatMessage({ id: 'inventarioSolicitudes.gestiones.tipo_compra', defaultMessage: 'Compra' }),
      CANCELACION: intl.formatMessage({
        id: 'inventarioSolicitudes.gestiones.tipo_cancelacion',
        defaultMessage: 'Rechazo',
      }),
    };
    return etiquetas[tipo] || tipo;
  };

  const claseTipo = (tipo) => {
    if (tipo === TIPO_CANCELACION) return 'badge badge-danger';
    if (tipo === TIPO_COMPRA) return 'badge badge-info';
    return 'badge badge-success';
  };

  const iconoTipo = (tipo) => {
    if (tipo === TIPO_CANCELACION) return 'fa fa-thumbs-down';
    if (tipo === TIPO_COMPRA) return 'fa fa-shopping-cart';
    return 'fa fa-exchange';
  };

  /** Fecha y hora: en un historial la hora distingue dos gestiones del mismo día, que es lo común. */
  const fechaHora = (valor) => {
    if (!valor) return '-';
    const fecha = LocaleFormat.formatDate(valor.substring(0, 10));
    return fecha + ' ' + valor.substring(11, 16);
  };

  const cantidadDe = (gestion) =>
    (gestion.lineas || []).reduce((total, linea) => total + Number(linea.cantidad || 0), 0);

  const nombreUbicacion = (u) => (u && u.nombre ? u.nombre : '-');

  if (!detalle) return null;

  const item = detalle.item || {};

  return (
    <Modal open={!!detalle} onClose={props.onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 300 }}>
      <Grow in={!!detalle}>
        <div tabIndex="-1" role="dialog">
          {/* `modal-lg` (800px) y no `modal-xl` (1140px): el contenido es una tabla de cinco columnas
              cortas, y a 1140 quedaba estirada con la mitad del ancho vacío. La doc reserva el `xl`
              para grillas y timelines, que es otra escala de contenido. */}
          <div className="modal-lg modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header bg-fleet">
                <h4 className="modal-title text-white mb-0">
                  <i className="fa fa-search align-middle icon-modal-title"></i>
                  <FormattedMessage id="inventarioSolicitudes.gestiones.titulo" defaultMessage=" Cómo se gestionó" />
                </h4>
                {/* La × de cerrar: va en TODOS los modales del sistema y es la salida
                    que la gente busca primero. */}
                <button type="button" className="close" aria-label="Close" onClick={props.onClose}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body modal-grey modal-mh ovf-x-hidden">
                <div className="mb-3">
                  <div style={{ fontWeight: 600 }}>
                    {item.numeroDeParte ? item.numeroDeParte + ' · ' : ''}
                    {item.descripcion}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    <FormattedMessage
                      id="inventarioSolicitudes.gestiones.contexto"
                      defaultMessage="Ticket {ticket} · Depósito {deposito} · Estado {estado} · Pedido {cantidad}, entregado {despachado}"
                      values={{
                        ticket: detalle.ticket ? detalle.ticket.id : '-',
                        deposito: detalle.ubicacion ? detalle.ubicacion.nombre : '-',
                        estado: <b>{detalle.estado}</b>,
                        cantidad: detalle.cantidad,
                        despachado: detalle.cantidadDespachada,
                      }}
                    />
                  </div>
                </div>

                {cargando && (
                  <div className="text-muted">
                    <FormattedMessage id="inventarioSolicitudes.gestiones.cargando" defaultMessage="Cargando…" />
                  </div>
                )}

                {!cargando && error && (
                  <div className="alert alert-danger mb-0">
                    <FormattedMessage
                      id="inventarioSolicitudes.gestiones.error"
                      defaultMessage="No se pudo leer el historial de esta solicitud."
                    />
                  </div>
                )}

                {/* Vacío EXPLÍCITO: una línea pendiente que nadie tocó no tiene historial, y una tabla vacía
                    se lee como un error de la pantalla en vez de como la respuesta correcta. */}
                {!cargando && !error && gestiones.length === 0 && (
                  <div className="alert alert-info mb-0">
                    <FormattedMessage
                      id="inventarioSolicitudes.gestiones.vacio"
                      defaultMessage="Todavía no se hizo ninguna gestión sobre esta solicitud."
                    />
                  </div>
                )}

                {!cargando && !error && gestiones.length > 0 && (
                  <div>
                    {/* SIN `table-responsive`: esa clase da scroll horizontal, que es justo lo que
                        hay que evitar. Acá no sobran columnas — lo que desbordaba era el motivo.

                        `tableLayout: 'fixed'` es lo que hace que el texto BAJE en vez de estirar la
                        tabla: sin él el ancho lo decide el contenido y ningún `width` de celda se
                        respeta. Con él mandan los porcentajes de abajo. */}
                    <table
                      className="table table-sm mb-0 bg-white"
                      style={{ tableLayout: 'fixed', width: '100%' }}
                    >
                      <thead>
                        <tr>
                          <th style={{ width: '15%' }}>
                            <FormattedMessage id="inventarioSolicitudes.gestiones.col_tipo" defaultMessage="Gestión" />
                          </th>
                          <th className="text-right" style={{ width: '10%' }}>
                            <FormattedMessage id="inventarioSolicitudes.gestiones.col_cantidad" defaultMessage="Cantidad" />
                          </th>
                          {/* La más ancha: es la que lleva el motivo, que es el texto largo. */}
                          <th style={{ width: '40%' }}>
                            <FormattedMessage id="inventarioSolicitudes.gestiones.col_detalle" defaultMessage="Detalle" />
                          </th>
                          <th style={{ width: '18%' }}>
                            <FormattedMessage id="inventarioSolicitudes.gestiones.col_fecha" defaultMessage="Fecha" />
                          </th>
                          <th style={{ width: '17%' }}>
                            <FormattedMessage id="inventarioSolicitudes.gestiones.col_usuario" defaultMessage="Responsable" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {gestiones.map((gestion) => (
                          <tr key={gestion.id}>
                            <td>
                              <span className={claseTipo(gestion.tipo)}>
                                <i className={iconoTipo(gestion.tipo) + ' mr-1'} />
                                {etiquetaTipo(gestion.tipo)}
                              </span>
                            </td>
                            <td className="text-right">{cantidadDe(gestion)}</td>
                            {/* `anywhere` y no solo `break-word`: el motivo puede ser una cadena de
                                255 caracteres SIN UN SOLO ESPACIO, y ahí no hay dónde cortar salvo
                                que se autorice cortar en cualquier punto. `break-word` va primero
                                para los navegadores que no entienden `anywhere`. */}
                            <td style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                              {gestion.tipo === TIPO_TRANSFERENCIA && (
                                <FormattedMessage
                                  id="inventarioSolicitudes.gestiones.detalle_transferencia"
                                  defaultMessage="Desde {origen} hacia {destino}"
                                  values={{
                                    origen: <b>{nombreUbicacion(gestion.ubicacionOrigen)}</b>,
                                    destino: <b>{nombreUbicacion(gestion.ubicacionDestino)}</b>,
                                  }}
                                />
                              )}
                              {gestion.tipo === TIPO_COMPRA && (
                                <FormattedMessage
                                  id="inventarioSolicitudes.gestiones.detalle_compra"
                                  defaultMessage="Stock nuevo, ingresado en {destino}"
                                  values={{ destino: <b>{nombreUbicacion(gestion.ubicacionDestino)}</b> }}
                                />
                              )}
                              {gestion.tipo === TIPO_CANCELACION && (
                                <FormattedMessage
                                  id="inventarioSolicitudes.gestiones.detalle_cancelacion"
                                  defaultMessage="Dado de baja, sin movimiento de stock"
                                />
                              )}

                              {/* El motivo va debajo del qué, no en su propia columna: solo lo tiene el
                                  rechazo, y una columna vacía en las otras dos filas ocupa lugar sin decir
                                  nada. */}
                              {gestion.motivo && (
                                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                  <i className="fa fa-quote-left mr-1" />
                                  {gestion.motivo}
                                </div>
                              )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>{fechaHora(gestion.fecha)}</td>
                            <td>{gestion.usuario || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="modal-footer modal-grey">
                {/* Un solo botón: es un modal de CONTENIDO, no una confirmación. Lleva el mismo check que
                    el resto de la familia (design system, "Modales de contenido"). */}
                <button type="button" className="btn btn-primary" onClick={props.onClose}>
                  <i className={ICONO_CONFIRMAR + ' mr-1'} />
                  <FormattedMessage id="inventarioSolicitudes.gestiones.cerrar" defaultMessage="Cerrar" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Grow>
    </Modal>
  );
};

export default InventarioSolicitudGestionesModal;
